import {Box, Flex, Image, Text} from 'exsportia-components'
import React, {FC, useMemo, useState} from 'react'

import {useStoreProduct} from '../../api/store'
import {useStoreCart} from '../../services/store/cart'
import {Button} from '../../ui/components/button/button'
import {useLocale} from '../../utils/i18n'
import {useModalActions} from '../../utils/modals'
import {matchVariant} from './store-shared'

/**
 * Pick a variant and add to the cart without leaving the catalogue (Dima
 * 2026-08-17). The shared modal already renders bottom-anchored with a slide-up
 * on mobile and centred on desktop, so this one component is both the mobile
 * sheet and the desktop popover.
 *
 * "View details" is always offered — this is a shortcut past the product page,
 * never a replacement for it.
 *
 * Modal content renders OUTSIDE the router (the provider sits above it), so
 * anything router-bound — navigation, and `useCurrencyFormat` via `useParams` —
 * is passed in by the caller rather than hooked here.
 */
export const ProductQuickBuy: FC<{
  venueId?: string
  stockItemId: string
  format: (value: number | null | undefined) => string
  onViewDetails: () => void
  onGoToCart: () => void
}> = ({venueId, stockItemId, format, onViewDetails, onGoToCart}) => {
  const {l} = useLocale()
  const cart = useStoreCart(venueId)
  const {closeAllModals} = useModalActions()
  const {data: product, isLoading} = useStoreProduct(venueId, stockItemId)
  const [selection, setSelection] = useState<Record<string, string>>({})
  const [added, setAdded] = useState(false)

  const matched = useMemo(
    () => (product?.hasVariants && product.options && product.variants
      ? matchVariant(product.options, product.variants, selection)
      : null),
    [product, selection],
  )

  if (isLoading || !product) {
    return null
  }

  const price = product.hasVariants ? (matched ? matched.variant.price : null) : product.price ?? null
  const compareAt = product.hasVariants
    ? matched?.variant.compareAtPrice ?? null
    : product.compareAtPrice ?? null
  const canAdd = product.hasVariants ? Boolean(matched?.variant.available) : product.inStock
  // Before a selection there is no single price — show the span the variants
  // cover so the sheet is never blank where the price belongs.
  const variantPrices = product.hasVariants
    ? Object.values(product.variants || {}).map((variant) => variant.price).filter((value) => typeof value === 'number')
    : []
  const priceLabel = price !== null
    ? format(price)
    : variantPrices.length
    ? (Math.min(...variantPrices) === Math.max(...variantPrices)
      ? format(Math.min(...variantPrices))
      : `${format(Math.min(...variantPrices))} – ${format(Math.max(...variantPrices))}`)
    : ''
  const matchedImageIndex = matched?.variant.imageIndex ?? null
  const image = matchedImageIndex !== null
    ? product.media[matchedImageIndex]
    : product.media[product.thumbnail] || product.media[0]

  const openProduct = () => {
    closeAllModals()
    onViewDetails()
  }

  const add = () => {
    if (!canAdd) {
      return
    }
    cart.add({
      stockItemId,
      variantId: matched ? matched.variantId : null,
      name: product.name,
      variantLabel: matched ? matched.variant.label : null,
      price: price || 0,
      thumbUrl: image || null,
    })
    setAdded(true)
  }

  return (
    // pb: the disabled generic footer normally supplies the modal's bottom
    // padding, so without it the buttons sit flush on the edge.
    <Box width='100%' pb='16px'>
      <Flex
        gap='16px'
        alignItems='flex-start'
      >
        <Box
          width='96px'
          height='96px'
          flexShrink={0}
          overflow='hidden'
          borderRadius='16px'
          backgroundColor='rgba(0, 16, 61, 0.06)'
          cursor='pointer'
          onClick={openProduct}
        >
          {image && (
            <Image
              width='100%'
              height='100%'
              style={{objectFit: 'cover'}}
              src={image}
            />
          )}
        </Box>
        <Flex
          flex='1'
          minWidth='0'
          flexDirection='column'
          gap='4px'
        >
          {/* The name lives here, not in the modal header — a long one rendered
              as a giant three-line headline up there. */}
          <Text
            text={product.name}
            settings={{fontWeight: 700, fontSize: '16px', lineHeight: '20px'}}
          />
          <Flex
            gap='8px'
            alignItems='baseline'
            flexWrap='wrap'
          >
            {priceLabel && (
              <Text
                text={priceLabel}
                settings={{fontWeight: 700, fontSize: '18px'}}
              />
            )}
            {compareAt !== null && price !== null && compareAt > price && (
              <Text
                text={format(compareAt)}
                settings={{textDecoration: 'line-through', color: 'rgba(0, 16, 61, 0.48)', fontSize: '14px'}}
              />
            )}
          </Flex>
          {!canAdd && !product.hasVariants && (
            <Text
              text={l('store-sold-out', {caseAction: 'capitalize'})}
              settings={{color: '#B3261E', fontWeight: 600, fontSize: '13px'}}
            />
          )}
        </Flex>
      </Flex>

      {product.hasVariants && (product.options || []).map((option) => (
        <Box
          key={option.name}
          mt='16px'
        >
          <Text
            text={option.name}
            settings={{color: 'rgba(0, 16, 61, 0.48)', fontSize: '13px'}}
          />
          <Flex
            mt='8px'
            gap='8px'
            flexWrap='wrap'
          >
            {option.values.map((value) => (
              <Box key={value}>
                <Button
                  type={selection[option.name] === value ? 'primary' : 'secondary'}
                  settings={{width: 'max-content'}}
                  onClick={() => {
                    setAdded(false)
                    setSelection({...selection, [option.name]: value})
                  }}
                >
                  {value}
                </Button>
              </Box>
            ))}
          </Flex>
        </Box>
      ))}

      {product.hasVariants && !matched && (
        <Box mt='12px'>
          <Text
            text={l('store-select-options', {caseAction: 'capitalize'})}
            settings={{fontSize: '13px', color: 'rgba(0, 16, 61, 0.48)'}}
          />
        </Box>
      )}

      {/* Confirmation stays in place — stacking a second modal on top of this
          one to say "added" is exactly what made the old flow feel heavy. */}
      {added && (
        <Box mt='12px'>
          <Text
            text={l('store-added-to-cart', {caseAction: 'capitalize'})}
            settings={{fontSize: '13px', fontWeight: 600, color: '#1E7A4C'}}
          />
        </Box>
      )}

      <Flex
        mt='20px'
        gap='8px'
        flexDirection='column'
      >
        {added ? (
          <Button
            type='primary'
            onClick={() => {
              closeAllModals()
              onGoToCart()
            }}
          >
            {`${l('store-go-to-cart', {caseAction: 'capitalize'})} (${cart.count})`}
          </Button>
        ) : (
          <Button
            type='primary'
            disabled={!canAdd}
            onClick={add}
          >
            {l('store-add-to-cart', {caseAction: 'capitalize'})}
          </Button>
        )}
        <Button
          type='secondary'
          onClick={openProduct}
        >
          {l('store-view-details', {caseAction: 'capitalize'})}
        </Button>
      </Flex>
    </Box>
  )
}
